import { cn } from "@/lib/utils";
import { useSidebar } from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { LayoutGrid, Settings } from "lucide-react";

interface CategoryItem {
  value: string;
  label: string;
  groupCount: number;
}

interface GlossaryCategoryNavProps {
  categories: CategoryItem[];
  isLoading: boolean;
  selected: string;
  onSelect: (slug: string) => void;
  onManageCategories?: () => void;
}

export function GlossaryCategoryNav({ categories, isLoading, selected, onSelect, onManageCategories }: GlossaryCategoryNavProps) {
  const { collapsed, isMobile } = useSidebar();
  const isCollapsed = collapsed && !isMobile;

  if (isLoading) {
    return (
      <div className={cn(
        "shrink-0 border-r bg-background p-3 space-y-2 transition-all duration-200 hidden md:block",
        isCollapsed ? "w-16" : "w-60"
      )}>
        {!isCollapsed && <Skeleton className="h-6 w-24 mb-4" />}
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className={cn("h-9", isCollapsed ? "w-10" : "w-full")} />
        ))}
      </div>
    );
  }

  const navItems = [
    { value: "all", label: "All", groupCount: categories.reduce((sum, c) => sum + c.groupCount, 0), isAll: true },
    ...categories.map(c => ({ ...c, isAll: false })),
  ];

  return (
    <nav
      className={cn(
        "shrink-0 border-r bg-background hidden md:flex md:flex-col transition-all duration-200",
        isCollapsed ? "w-16" : "w-60"
      )}
      aria-label="Glossary category navigation"
      data-testid="nav-glossary-categories"
    >
      {!isCollapsed && (
        <div className="px-4 pt-8 pb-3 border-b flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold" data-testid="text-glossary-nav-title">
            Categories
          </h2>
          {onManageCategories && (
            <Button
              size="icon"
              variant="ghost"
              onClick={onManageCategories}
              title="Manage categories"
              data-testid="button-manage-categories-nav"
            >
              <Settings className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}

      <div className={cn("flex-1 overflow-y-auto", isCollapsed ? "p-2 pt-4" : "p-3")}>
        <ul className="space-y-1">
          {navItems.map((item) => {
            const isActive = selected === item.value;

            return (
              <li key={item.value}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onSelect(item.value)}
                      aria-label={`${item.label} (${item.groupCount} concepts)`}
                      className={cn(
                        "w-full flex items-center rounded-md transition-colors",
                        isCollapsed ? "justify-center p-2" : "gap-2 px-3 py-2",
                        isActive
                          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          : "text-muted-foreground hover-elevate"
                      )}
                      data-testid={`button-glossary-cat-${item.value}`}
                      data-active={isActive}
                    >
                      {item.isAll ? (
                        <LayoutGrid className={cn(
                          "h-4 w-4 shrink-0",
                          isActive ? "text-primary" : "text-muted-foreground"
                        )} />
                      ) : isCollapsed ? (
                        <span className={cn(
                          "text-xs font-medium shrink-0",
                          isActive ? "text-sidebar-accent-foreground" : "text-muted-foreground"
                        )}>
                          {item.label.slice(0, 2).toUpperCase()}
                        </span>
                      ) : null}
                      {!isCollapsed && (
                        <span className="truncate text-sm flex-1 text-left">{item.label}</span>
                      )}
                      {!isCollapsed && (
                        <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0" data-testid={`badge-glossary-cat-count-${item.value}`}>
                          {item.groupCount}
                        </Badge>
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" align="center" hidden={!isCollapsed}>
                    {item.label} ({item.groupCount})
                  </TooltipContent>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}

export function GlossaryCategoryNavMobile({ categories, isLoading, selected, onSelect }: GlossaryCategoryNavProps) {
  if (isLoading) {
    return (
      <div className="md:hidden pb-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-16" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-20 shrink-0 rounded-full" />
          ))}
        </div>
      </div>
    );
  }

  const navItems = [
    { value: "all", label: "All", groupCount: categories.reduce((sum, c) => sum + c.groupCount, 0) },
    ...categories,
  ];

  return (
    <div className="md:hidden pb-3" data-testid="nav-glossary-categories-mobile">
      <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4">
        <span className="text-sm font-medium text-muted-foreground shrink-0">Category:</span>
        {navItems.map((item) => {
          const isActive = selected === item.value;

          if (isActive) {
            return (
              <button
                key={item.value}
                onClick={() => onSelect(item.value)}
                className="shrink-0 flex items-center gap-1.5 bg-primary text-primary-foreground font-medium text-sm px-3 py-1 rounded-full transition-colors"
                data-testid={`button-glossary-cat-mobile-${item.value}`}
                data-active={isActive}
              >
                {item.label}
                <span className="text-primary-foreground/70 text-xs" data-testid={`text-glossary-cat-count-mobile-${item.value}`}>({item.groupCount})</span>
              </button>
            );
          }

          return (
            <button
              key={item.value}
              onClick={() => onSelect(item.value)}
              className="shrink-0 flex items-center gap-1 text-sm text-muted-foreground px-2.5 py-1 rounded-full hover-elevate transition-colors"
              data-testid={`button-glossary-cat-mobile-${item.value}`}
              data-active={isActive}
              title={item.label}
            >
              {item.label}
              <span className="text-xs" data-testid={`text-glossary-cat-count-mobile-${item.value}`}>({item.groupCount})</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
